/* brd-2026-newsletter-popup.js
 *
 * One-time newsletter popup controller. Responsibilities:
 *   1. Read `data-delay-seconds` and wait that long before opening.
 *   2. Suppress display if a localStorage flag exists and we're inside
 *      the configured cooldown window (`data-cooldown-days`).
 *   3. Open the modal with focus management — focus moves into the panel
 *      and is trapped via TAB cycle until close.
 *   4. ESC + backdrop click + close button all dismiss.
 *   5. Persist a localStorage state object `{ status, ts }` on dismiss
 *      OR on successful subscribe (success path set from the Liquid).
 *   6. If the page just reloaded after a successful subscribe (Shopify's
 *      form posts back), open in success-state immediately.
 *
 * Created: 2026-05-11
 */
(function () {
  'use strict';

  var STATE_KEY_DEFAULT = 'brd-newsletter-popup-state';
  var DAY_MS = 24 * 60 * 60 * 1000;

  /* Read the persisted state (status: 'dismissed' | 'subscribed', ts: epoch). */
  function readState(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeState(key, status) {
    try {
      var state = { status: status, ts: Date.now() };
      localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
      /* localStorage might be disabled (Safari private mode pre-15, etc.).
         The popup will just show again next visit — not ideal but not
         broken either. Silently fall back. */
    }
  }

  /* Should we display the popup? Yes if no state OR state is past cooldown. */
  function shouldShow(state, cooldownDays) {
    if (!state) return true;
    // cooldownDays = 0 → lifetime suppression once dismissed/subscribed.
    if (cooldownDays === 0) return false;
    var age = Date.now() - (state.ts || 0);
    return age >= (cooldownDays * DAY_MS);
  }

  /* Tab focus trap inside the panel. */
  function getFocusable(panel) {
    return panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function trapFocus(panel, e) {
    if (e.key !== 'Tab') return;
    var focusable = getFocusable(panel);
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* Open / close. */
  function open(popup, opts) {
    opts = opts || {};
    var panel = popup.querySelector('.brd-newsletter-popup__panel');
    if (!panel) return;

    // Remember which element had focus so we can restore it on close.
    popup._brdReturnFocus = document.activeElement;

    popup.removeAttribute('hidden');
    popup.setAttribute('aria-hidden', 'false');

    if (opts.success) popup.classList.add('is-success');

    // Force a paint before adding `.is-open` so the CSS transition fires.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        popup.classList.add('is-open');
      });
    });

    document.body.classList.add('brd-newsletter-popup-open');

    // Move focus into the panel (the close button is a safe first target).
    var focusTarget = panel.querySelector('[data-brd-np-close]')
                   || panel.querySelector('input, button')
                   || panel;
    setTimeout(function () { focusTarget.focus(); }, 50);

    // Wire ESC + tab trap.
    popup._brdKeydown = function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(popup, 'dismissed');
      } else {
        trapFocus(panel, e);
      }
    };
    document.addEventListener('keydown', popup._brdKeydown);
  }

  function close(popup, status) {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('brd-newsletter-popup-open');

    if (popup._brdKeydown) {
      document.removeEventListener('keydown', popup._brdKeydown);
      popup._brdKeydown = null;
    }

    // Persist state — only on dismiss; subscribe path writes its own flag
    // inline in Liquid so it sticks even before this script runs.
    if (status === 'dismissed') {
      var key = popup.getAttribute('data-state-key') || STATE_KEY_DEFAULT;
      writeState(key, 'dismissed');
    }

    // After the CSS transition completes, hide entirely.
    setTimeout(function () {
      popup.setAttribute('hidden', '');
      if (popup._brdReturnFocus && popup._brdReturnFocus.focus) {
        popup._brdReturnFocus.focus();
      }
    }, 400);
  }

  function init() {
    var popup = document.querySelector('.brd-newsletter-popup');
    if (!popup) return;

    var key = popup.getAttribute('data-state-key') || STATE_KEY_DEFAULT;
    var delay = parseInt(popup.getAttribute('data-delay-seconds') || '5', 10);
    var cooldown = parseInt(popup.getAttribute('data-cooldown-days') || '30', 10);
    var state = readState(key);

    // If Liquid signaled a successful subscribe on this request, open the
    // popup straight to its success view — bypasses cooldown by design.
    if (window.__brdNewsletterShowSuccess) {
      open(popup, { success: true });
    } else if (shouldShow(state, cooldown)) {
      setTimeout(function () { open(popup); }, delay * 1000);
    }

    // Wire all close triggers (close button + backdrop + ghost dismiss).
    var closers = popup.querySelectorAll('[data-brd-np-close]');
    Array.prototype.forEach.call(closers, function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        close(popup, 'dismissed');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
